import React from 'react';
import { AlertTriangle } from 'lucide-react'; // Optional icon

const Disclaimer = () => {
  const lastUpdatedDate = "May 29, 2025"; // REPLACE WITH ACTUAL LAST UPDATED DATE

  // Helper for consistent section styling
  const Section = ({ title, children, id }) => (
    <section id={id} className="mb-8 scroll-mt-20"> {/* scroll-mt for anchor links */}
      {title && (
        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-300 dark:border-gray-700 pb-2">
          {title}
        </h2>
      )}
      <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </div>
    </section>
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <header className="mb-10 text-center">
          <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-yellow-500 dark:text-yellow-400" />
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white">
            Disclaimer for <span className="text-blue-600 dark:text-blue-400">TabDivider</span> Services
          </h1>
          <p className="mt-4 text-md sm:text-lg text-gray-600 dark:text-gray-400">
            Last Updated: {lastUpdatedDate}
          </p>
          <p className="mt-2 text-md sm:text-lg text-gray-600 dark:text-gray-400">
            Please read this disclaimer carefully before using our services.
          </p>
        </header>

        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-6 sm:p-8 lg:p-10">
          <Section id="general-information">
            <p className="font-semibold text-lg sm:text-xl">
              The information provided by TabDivider ("we," "us," or "our") on <a href="https://tabdivider.com" className="text-blue-600 dark:text-blue-400 hover:underline">tabdivider.com</a> (the "Site") and through our bill splitting and shared expense management application (the "Service") is for general informational and convenience purposes only.
            </p>
            <p>
              All information on the Site and within the Service is provided in good faith. However, we make no representation or warranty of any kind, express or implied, regarding the accuracy, adequacy, validity, reliability, availability, or completeness of any information on the Site or Service. This includes data entered manually by users, data extracted via Optical Character Recognition (OCR), and any calculations performed by the Service.
            </p>
          </Section>

          <Section title="No Professional Advice" id="no-professional-advice">
            <p>
              The information provided by TabDivider is not intended as, and shall not be understood or construed as, professional financial, accounting, legal, or tax advice. The Service is a tool designed to assist with organizing and calculating shared expenses, but it is not a substitute for advice from a qualified professional.
            </p>
            <p>
              Before making any financial decisions or implementing any financial strategy, you should consult with a professional to ensure that any information provided by the Service is appropriate for your specific situation. Your reliance on any information provided by TabDivider is solely at your own risk.
            </p>
          </Section>

          <Section title="Limitation of Liability" id="limitation-liability">
            <p className="font-bold uppercase text-red-600 dark:text-red-400">
              Under no circumstance shall TabDivider, its affiliates, directors, employees, agents, suppliers, or licensors have any liability to you for any loss or damage of any kind incurred as a result of the use of the Site or Service or reliance on any information provided on the Site or Service.
            </p>
            <p>
              This includes, but is not limited to, errors in calculations, inaccuracies in OCR data extraction, or any financial decisions made based on the information provided by the tool. Your use of the Site and Service, and your reliance on any information therein, is solely at your own risk.
            </p>
          </Section>

          <Section title="Accuracy of OCR Technology" id="ocr-accuracy">
            <p>
              Our Optical Character Recognition (OCR) feature, used for scanning receipts and extracting data, is provided as a convenience tool. While we strive to make this feature as accurate as possible, OCR technology is not infallible and may be subject to errors, inaccuracies, or omissions.
            </p>
            <p>
              <strong className="text-gray-800 dark:text-gray-100">Users are solely responsible for verifying the accuracy and completeness of all data extracted via the OCR feature before relying on it for calculations or financial decisions.</strong> TabDivider is not responsible for any discrepancies, errors, or omissions in the data extracted by the OCR technology. We recommend manually reviewing all OCR-extracted information.
            </p>
          </Section>

          <Section title="External Links Disclaimer" id="external-links">
            <p>
              The Site and Service may contain (or you may be sent through the Site or Service) links to other websites or content belonging to or originating from third parties or links to websites and features. Such external links are not investigated, monitored, or checked for accuracy, adequacy, validity, reliability, availability, or completeness by us.
            </p>
            <p>
              We do not warrant, endorse, guarantee, or assume responsibility for the accuracy or reliability of any information offered by third-party websites linked through the site or any website or feature linked in any banner or other advertising. We will not be a party to or in any way be responsible for monitoring any transaction between you and third-party providers of products or services.
            </p>
          </Section>

           <Section title="No Guarantees of Results" id="no-guarantees">
            <p>
              While TabDivider is designed to simplify the process of splitting bills and managing shared expenses, we do not guarantee any specific financial outcomes or results from using the Service. The effectiveness of the tool depends on accurate data input by users and proper verification of all information.
            </p>
            <p>
              TabDivider is not responsible for interpersonal disputes that may arise from shared expense management, even when using our Service. The tool is intended to aid in calculations and organization, not to mediate or resolve personal disagreements.
            </p>
          </Section>

          <Section title="User Responsibility" id="user-responsibility">
            <p>
              You acknowledge and agree that you are solely responsible for:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>The accuracy and completeness of any information you input into the Service.</li>
              <li>Verifying all calculations and data, especially that derived from OCR.</li>
              <li>Any financial decisions made based on the information provided by or calculated by the Service.</li>
              <li>Complying with all applicable laws and regulations related to your financial activities.</li>
            </ul>
          </Section>

          <Section title="Consent" id="consent">
            <p>
              By using our Site and Service, you hereby consent to this Disclaimer and agree to its terms.
            </p>
          </Section>

          <Section title="Updates" id="updates">
            <p>
              Should we update, amend or make any changes to this document, those changes will be prominently posted here. You are advised to review this Disclaimer periodically for any changes.
            </p>
          </Section>

          <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>
              If you require any more information or have any questions about our site's disclaimer, please feel free to contact us by email at <a href="mailto:support@tabdivider.com" className="text-blue-600 dark:text-blue-400 hover:underline">support@tabdivider.com</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;